import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Building2, LayoutDashboard, GitBranch, FileText, PiggyBank, Package,
} from 'lucide-react';
import SectionWrapper from '../../components/marketing/SectionWrapper';
import FeatureCard from '../../components/marketing/FeatureCard';
import ScreenshotShowcase from '../../components/marketing/ScreenshotShowcase';
import CTABanner from '../../components/marketing/CTABanner';
import { ALL_PHOTOS } from '../../components/marketing/photoPool';

export default function NetworksPage() {
  const { t } = useTranslation('marketing');

  const features = [
    { icon: LayoutDashboard, title: t('networks.feature1_title'), description: t('networks.feature1_desc'), color: '#7c3aed' },
    { icon: GitBranch, title: t('networks.feature2_title'), description: t('networks.feature2_desc'), color: '#0891b2' },
    { icon: FileText, title: t('networks.feature3_title'), description: t('networks.feature3_desc'), color: '#16a34a' },
    { icon: PiggyBank, title: t('networks.feature4_title'), description: t('networks.feature4_desc'), color: '#d97706' },
    { icon: Package, title: t('networks.feature5_title'), description: t('networks.feature5_desc'), color: '#059669' },
  ];

  return (
    <>
      <section className="pt-24 pb-14 bg-gradient-to-br from-purple-50/60 via-white to-violet-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-50 border border-purple-100 text-purple-700 text-xs font-semibold tracking-wide uppercase mb-6">
              <Building2 className="w-3.5 h-3.5" />
              {t('networks.section_title')}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">{t('networks.title')}</h1>
            <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto">{t('networks.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <SectionWrapper background="white">
        <ScreenshotShowcase
          imageSrcs={ALL_PHOTOS}
          alt="Network management overview"
          direction="left"
          title={t('networks.feature1_title')}
          subtitle={t('networks.feature1_desc')}
          features={[
            { text: t('networks.feature2_title') + ' \u2014 ' + t('networks.feature2_desc') },
            { text: t('networks.feature3_title') + ' \u2014 ' + t('networks.feature3_desc') },
            { text: t('networks.feature4_title') + ' \u2014 ' + t('networks.feature4_desc') },
            { text: t('networks.feature5_title') + ' \u2014 ' + t('networks.feature5_desc') },
          ]}
          accentColor="#7c3aed"
          placeholderIcon={Building2}
        />
      </SectionWrapper>

      <SectionWrapper background="gray">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((f, i) => (
            <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} color={f.color} index={i} />
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper title={t('networks.feature2_title')} background="white">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6"
          >
            <div className="space-y-3">
              <div className="bg-purple-50/80 rounded-lg p-3 border border-purple-100">
                <div className="flex items-center gap-2 mb-2.5">
                  <Building2 className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-700">{t('networks.section_title')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 ps-3">
                  {[1, 2].map(city => (
                    <div key={city} className="bg-blue-50/80 rounded-lg p-2.5 border border-blue-100">
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="w-2 h-2 rounded-full bg-blue-400" />
                        <span className="text-xs font-medium text-blue-700">{t('networks.hierarchy_city')} {city}</span>
                      </div>
                      <div className="space-y-1 ps-2.5">
                        {[1, 2, 3].map(kg => (
                          <div key={kg} className="bg-green-50/80 rounded px-2 py-1 text-[11px] text-green-700 border border-green-100 flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                            {t('networks.hierarchy_kg')} {kg}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </SectionWrapper>

      <CTABanner
        title={t('cta.title')}
        subtitle={t('cta.subtitle')}
        buttonText={t('cta.button')}
      />
    </>
  );
}
